/**
 * Msgpack message decoding for MCAP channels.
 *
 * Msgpack is self-describing, like JSON: the channel's (jsonschema) schema is
 * descriptive metadata only, so decoding needs no schema compilation. It is
 * decoded with msgpackr in Maps mode (`mapsAsObjects: false`) because real
 * recordings contain maps with *binary* keys — notably msgpack-numpy encoded
 * ndarrays — which object-mode decoders reject outright.
 *
 * The decoded value is then normalized for message-path access:
 *
 * - Maps become plain objects, with binary keys decoded as UTF-8.
 * - msgpack-numpy ndarray wrappers, `{nd: true, type, kind, shape, data}`,
 *   are unwrapped into the TypedArray matching their dtype, so byte fields
 *   (e.g. compressed video frames) are directly usable. Wrappers that can't
 *   be unwrapped faithfully (unsupported dtype, multi-dimensional shape,
 *   size mismatch) are left as normalized plain objects.
 */
import { Unpackr } from "msgpackr";

import { LoggerService } from "@/shared/services";

const textDecoder = new TextDecoder();

// int64AsType "bigint" preserves 64-bit integers exactly. Decoding them as
// numbers would silently corrupt any value above 2^53, and it matches the CDR
// (ROS) readers, which already surface int64/uint64 fields as bigints. JSON
// channels still yield numbers (JSON.parse cannot produce bigints), so this
// prefers correctness over cross-encoding uniformity.
const unpackr = new Unpackr({ int64AsType: "bigint", mapsAsObjects: false });

/**
 * TypedArray constructors by numpy dtype string ("|" no/any byte order, "<"
 * little-endian). Big-endian dtypes are intentionally absent: TypedArrays are
 * platform-endian (little-endian everywhere that runs this code), so
 * big-endian data cannot be viewed directly.
 */
const DTYPE_TO_TYPED_ARRAY = {
  "|i1": Int8Array,
  "|u1": Uint8Array,
  "<f4": Float32Array,
  "<f8": Float64Array,
  "<i2": Int16Array,
  "<i4": Int32Array,
  "<u2": Uint16Array,
  "<u4": Uint32Array,
} as const;

type SupportedDtype = keyof typeof DTYPE_TO_TYPED_ARRAY;

function isSupportedDtype(dtype: string): dtype is SupportedDtype {
  return dtype in DTYPE_TO_TYPED_ARRAY;
}

/** Decode one msgpack-encoded MCAP message into message-path-accessible data. */
export function decodeMsgpackMessage(view: ArrayBufferView): unknown {
  const bytes = new Uint8Array(view.buffer, view.byteOffset, view.byteLength);
  return normalizeValue(unpackr.unpack(bytes) as unknown);
}

function normalizeValue(value: unknown): unknown {
  if (value instanceof Map) {
    const record: Record<string, unknown> = {};
    for (const [key, entry] of value as Map<unknown, unknown>) {
      const normalizedKey = normalizeKey(key);
      // Distinct source keys (e.g. a string key and a UTF-8 binary key, or two
      // binary keys) can normalize to the same string and silently clobber one
      // another. That is a data problem worth surfacing: warn and keep the last.
      if (Object.hasOwn(record, normalizedKey)) {
        LoggerService.warn(
          `msgpack map has multiple keys normalizing to "${normalizedKey}"; keeping the last, earlier values are dropped.`,
        );
      }
      record[normalizedKey] = normalizeValue(entry);
    }
    return unwrapNdarray(record) ?? record;
  }
  if (Array.isArray(value)) {
    return value.map(normalizeValue);
  }
  return value;
}

function normalizeKey(key: unknown): string {
  if (typeof key === "string") {
    return key;
  }
  if (key instanceof Uint8Array) {
    return textDecoder.decode(key);
  }
  return String(key);
}

/**
 * Unwrap a msgpack-numpy ndarray into the TypedArray matching its dtype, or
 * return `undefined` when `record` is not one (or not one that can be
 * unwrapped faithfully).
 */
function unwrapNdarray(
  record: Record<string, unknown>,
): ArrayBufferView | undefined {
  const { nd, type, shape, data } = record;
  if (nd !== true || !(data instanceof Uint8Array) || !Array.isArray(shape)) {
    return undefined;
  }

  // msgpack-numpy writes the dtype as a string; be tolerant of producers
  // that pack it as bytes.
  const dtype = type instanceof Uint8Array ? textDecoder.decode(type) : type;
  if (typeof dtype !== "string" || !isSupportedDtype(dtype)) {
    return undefined;
  }

  // Only 1-D arrays unwrap losslessly; a flat TypedArray would silently drop
  // the dimensions of anything higher-dimensional. A dimension packed as a
  // 64-bit int decodes to a bigint (see int64AsType above), so accept both.
  if (shape.length !== 1) {
    return undefined;
  }
  const dim: unknown = shape[0];
  if (typeof dim !== "number" && typeof dim !== "bigint") {
    return undefined;
  }
  const length = Number(dim);

  const ctor = DTYPE_TO_TYPED_ARRAY[dtype];
  if (data.byteLength !== length * ctor.BYTES_PER_ELEMENT) {
    return undefined;
  }

  // Multi-byte views require element-aligned offsets, which a view into the
  // middle of a message buffer rarely has; copy when misaligned. The copy
  // via `new Uint8Array` is deliberate: Buffer (a Uint8Array subclass in
  // tests) overrides `slice` to alias memory instead of copying.
  const aligned =
    data.byteOffset % ctor.BYTES_PER_ELEMENT === 0
      ? data
      : new Uint8Array(data);
  return new ctor(aligned.buffer, aligned.byteOffset, length);
}
